/*
Thin fetch wrapper around the backend HTTP API. Injects the bearer
token from auth state, parses JSON, and surfaces non-2xx responses as
Error instances with a tagged code so callers can switch on them.
*/

const kStorageKey = "nfcreader.token";	// localStorage key holding the bearer token

// Side-channel for components that want to react to a 401
// (typically AuthProvider, which clears the in-memory session and
// stamps a "session expired" flag for the LoginPage banner). Wired
// as a single handler — there's only one auth context — so the
// register/clear is symmetric and lifecycle-safe.
let fUnauthorizedHandler: (() => void) | null = null;

/*
Registers (or clears) the global 401 handler. Pass null to detach.
Mounted via AuthProvider's useEffect; api() invokes it before
throwing the ApiError so the caller still sees the failed promise.
*/
export function setUnauthorizedHandler(
	inHandler:	(() => void) | null
): void
{
	fUnauthorizedHandler = inHandler;
}

/*
Stores the bearer token in localStorage so subsequent requests carry it.
*/
export function setToken(
	inToken:	string | null	// Token to store (null clears it)
): void
{
	if (inToken === null)
	{
		localStorage.removeItem(kStorageKey);
		return;
	}

	localStorage.setItem(kStorageKey, inToken);
}

/*
Reads the stored bearer token, or null when the user is not signed in.
*/
export function getToken(): string | null
{
	return localStorage.getItem(kStorageKey);
}

/*
Error thrown by api() on non-2xx responses.
*/
export class ApiError extends Error
{
	public readonly status:	number;	// HTTP status
	public readonly code:	string;	// Server-supplied error code, or "http_error" by default

	constructor(
		inStatus:	number,	// HTTP status
		inCode:		string,	// Tagged error code
		inMessage:	string	// Human-readable message
	)
	{
		super(inMessage);
		this.status	= inStatus;
		this.code	= inCode;
	}
}

/*
Performs an HTTP request against the backend and returns the parsed
JSON body. The path is appended to /api so callers don't need to.
*/
export async function api<T>(
	inMethod:	"GET" | "POST" | "PATCH" | "DELETE",	// HTTP method
	inPath:		string,									// Path under /api (e.g. "/auth/verify-code")
	inBody?:	unknown									// Optional JSON body
): Promise<T>
{
	const vToken	= getToken();					// Bearer token if signed in
	const vHeaders:	Record<string, string> = {};	// Outgoing headers
	let   vPayload:	BodyInit | undefined = undefined;

	if (inBody !== undefined)
	{
		vHeaders["Content-Type"]	= "application/json";
		vPayload					= JSON.stringify(inBody);
	}

	if (vToken !== null)
	{
		vHeaders["Authorization"] = `Bearer ${vToken}`;
	}

	// Spread `body` only when set — `exactOptionalPropertyTypes` bans
	// passing `undefined` for an optional RequestInit field.
	const vInit: RequestInit =
	{
		method:		inMethod,
		headers:	vHeaders,
		...(vPayload !== undefined ? { body: vPayload } : {}),
	};

	const vRes = await fetch(`/api${inPath}`, vInit);

	if (vRes.status === 204)
	{
		return undefined as T;
	}

	const vText = await vRes.text();			// Raw response body
	const vData = vText.length > 0 ? JSON.parse(vText) : null;	// Parsed JSON

	if (!vRes.ok)
	{
		const vCode		= (vData && typeof vData.error === "string") ? vData.error : "http_error";
		const vMessage	= (vData && typeof vData.message === "string") ? vData.message : vRes.statusText;

		// Token-expiry side-effect: a 401 from any authenticated call
		// means the bearer token was rejected by the server (expired,
		// revoked, the user was deleted, …). Trigger the registered
		// handler so the SPA can clear its in-memory session and
		// surface the "session expired" banner on /login. Unauthenticated
		// endpoints (e.g. POST /auth/verify-code) also return 401 on bad
		// credentials — but in that case there's no stored token yet
		// (getToken() === null), so we suppress the side-effect.
		if (vRes.status === 401 && getToken() !== null && fUnauthorizedHandler !== null)
		{
			fUnauthorizedHandler();
		}

		throw new ApiError(vRes.status, vCode, vMessage);
	}

	return vData as T;
}

/*
GET helper for binary endpoints that stream a file (e.g. the
GET /api/contents/:id/parts/:name endpoint). Returns the raw blob
along with the filename announced by the server's
Content-Disposition header (or null if absent). Auth + base-URL
handling matches the JSON `api()` wrapper.
*/
export async function apiBlob(
	inPath:	string			// Path under /api (e.g. "/contents/<id>/parts/gt4")
): Promise<{ blob: Blob; filename: string | null }>
{
	const vToken	= getToken();					// Bearer token if signed in
	const vHeaders:	Record<string, string> = {};	// Outgoing headers

	if (vToken !== null)
	{
		vHeaders["Authorization"] = `Bearer ${vToken}`;
	}

	const vRes = await fetch(`/api${inPath}`, { method: "GET", headers: vHeaders });

	if (!vRes.ok)
	{
		// Best-effort error decoding — non-binary error responses are
		// JSON in the same { error, message } shape used by api().
		let vCode	= "http_error";
		let vMessage	= vRes.statusText;

		try
		{
			const vText = await vRes.text();			// Error body if any
			const vData = vText.length > 0 ? JSON.parse(vText) : null;	// Parsed JSON

			if (vData && typeof vData.error === "string")		{ vCode = vData.error; }
			if (vData && typeof vData.message === "string")	{ vMessage = vData.message; }
		}
		catch
		{
			// Body wasn't JSON — keep the defaults.
		}

		// Same token-expiry trigger as api(). See its comment for the
		// "stored token vs no token" rationale.
		if (vRes.status === 401 && getToken() !== null && fUnauthorizedHandler !== null)
		{
			fUnauthorizedHandler();
		}

		throw new ApiError(vRes.status, vCode, vMessage);
	}

	return {
		blob:		await vRes.blob(),
		filename:	parseDispositionFilename(vRes.headers.get("Content-Disposition")),
	};
}

/*
Triggers a browser "Save As" with the given blob + filename by
creating a temporary object URL, programmatically clicking a hidden
anchor, and revoking the URL on next tick. Matches the standard
recipe for SPA file downloads.
*/
export function triggerDownload(
	inBlob:		Blob,	// Bytes to save
	inFilename:	string	// Suggested filename
): void
{
	const vUrl		= URL.createObjectURL(inBlob);	// Temporary blob URL
	const vAnchor	= document.createElement("a");	// Hidden trigger

	vAnchor.href		= vUrl;
	vAnchor.download	= inFilename;
	document.body.appendChild(vAnchor);
	vAnchor.click();
	document.body.removeChild(vAnchor);
	// Defer revoke to give the browser a chance to start the download
	// — some engines abort if the URL disappears too quickly.
	setTimeout(() => URL.revokeObjectURL(vUrl), 0);
}

/*
Extracts the filename parameter from a Content-Disposition response
header. Tolerates the common `filename="…"` form; returns null when
the header is absent or doesn't carry a quoted filename.
*/
function parseDispositionFilename(
	inHeader:	string | null	// Raw header value
): string | null
{
	if (inHeader === null)
	{
		return null;
	}

	const vMatch = /filename="([^"]+)"/.exec(inHeader);	// Quoted filename token

	return vMatch !== null && vMatch[1] !== undefined ? vMatch[1] : null;
}
